import { useEffect } from 'react'
import { useConnectionStore } from '@/stores/connectionStore'
import { useDataSourceQuery } from '@/hooks/useDataSources'

/**
 * Hook to automatically restore the last active connection on app load
 */
export function useLastConnection() {
  const { 
    lastActiveConnectionId, 
    activeConnection, 
    setActiveConnection 
  } = useConnectionStore()

  // Only query the datasource if we have a lastActiveConnectionId but no activeConnection
  const shouldFetch = !!lastActiveConnectionId && !activeConnection
  
  const { 
    data: dataSource, 
    isLoading,
    error 
  } = useDataSourceQuery(shouldFetch ? lastActiveConnectionId : '')

  useEffect(() => {
    // If we have a lastActiveConnectionId but no activeConnection, 
    // and we successfully fetched the datasource data, restore the connection
    if (lastActiveConnectionId && !activeConnection && dataSource) {
      const connectionData = {
        id: String(dataSource.id),
        name: dataSource.displayName || `${dataSource.host}:${dataSource.port}`,
        type: dataSource.databaseType === 'MySQL' ? 'mysql' as const : 'postgresql' as const,
        host: dataSource.host || '',
        port: dataSource.port || (dataSource.databaseType === 'MySQL' ? 3306 : 5432),
        username: dataSource.username || '',
        password: '', // We don't store passwords in the UI store
        database: dataSource.defaultDatabase || undefined,
        createdAt: dataSource.createdAt || new Date().toISOString(),
        updatedAt: dataSource.updatedAt || new Date().toISOString(),
      }
      
      setActiveConnection(connectionData)
    }
  }, [lastActiveConnectionId, activeConnection, dataSource, isLoading, setActiveConnection])

  useEffect(() => {
    // Handle error case - if we can't fetch the last connection, clear it
    if (error && lastActiveConnectionId && !activeConnection) {
      console.warn('Failed to restore last connection:', error)
      // Don't show error toast on app load, just silently clear
    }
  }, [error, lastActiveConnectionId, activeConnection])

  return {
    isRestoring: isLoading && !!lastActiveConnectionId && !activeConnection,
    restoredConnection: activeConnection,
    restorationError: error
  }
}
